import React, {
	useState,
	useMemo,
	useEffect,
	useRef,
} from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import Icon, { ICONS, COLORS } from './icon';
import * as MediaActions from '../../stores/actions/media';
import * as NotesActions from '../../stores/actions/notes';
import Avatar, { AvatarUser } from './avatar';
import Socket, { ConnectedUser } from '../../connection/socket';
import { getUserProfileWithId, uniqueArray, listToArray } from '../../utils/utils';
import { GreenroomState } from '../../stores/types/types';
import { TeleprompterInterface } from '../../types/interfaces';
import { Invitation, Profile } from '../../stores/reducers/invitations';
import ShareList from './share-list';
import useTeleprompters from '../../hooks/use-teleprompters';
import { MediaItem } from '../../stores/reducers/media';

export interface OwnProps {
	shareType: string;
	popoverToggled: Function;
	socket?: Socket | null;
	popoverClosed: Function;
	forNote?: boolean;
	forGlobalNote?: boolean;
	constrainToParent?: boolean;
}

export const SHARE_TYPE = {
	MEDIA: 'media',
	VIDEO: 'video',
	NOTE: 'note',
	SHOT_NOTE: 'shotNote',
	SLIDE_NOTE: 'slideNote',
};

export const SHARE_PERMISSIONS = {
	CAN_VIEW: 'canView',
	CAN_EDIT: 'canEdit',
};

interface UserMap {
	[key: string]: ConnectedUser;
}

export default function ShareWith(props: OwnProps) {
	const {
		shareType,
		popoverToggled,
		socket,
		forNote,
		forGlobalNote,
		constrainToParent = false,
	} = props;

	const dispatch = useDispatch();
	const { shareTeleprompter } = useTeleprompters(socket || null);

	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
	const connectedUsers = useSelector(
		(state: GreenroomState) => state.SessionReducer.connectedUsers,
	);
	const media = useSelector((state: GreenroomState) => state.MediaReducer.media);
	const selectedMedia = useSelector((state: GreenroomState) => state.MediaReducer.selectedMedia);
	const invitations = useSelector((state: GreenroomState) => state.InvitationsReducer.invitations);
	const workingAllNote = useSelector((state: GreenroomState) => state.NotesReducer.workingAllNote);
	const workingNote = useSelector((state: GreenroomState) => state.NotesReducer.workingNote);
	const teleprompters = useSelector(
		(state: GreenroomState) => state.TeleprompterReducer.teleprompters,
	);
	const selectedTeleprompters = useSelector(
		(state: GreenroomState) => state.TeleprompterReducer.selectedTeleprompters,
	);
	const [sharePopoverOpen, setSharePopoverOpen] = useState(false);
	const [sharePopoverHidden, setSharePopoverHidden] = useState(true);
	const [showAll, setShowAll] = useState(false);
	const [hideShowAll, setHideShowAll] = useState(false);

	const selectedItem = useMemo(() => {
		if (forNote) {
			if (shareType === SHARE_TYPE.NOTE) {
				return workingNote;
			}
			if (shareType === SHARE_TYPE.SHOT_NOTE) {
				return teleprompters.find((teleprompter) => teleprompter.id === selectedTeleprompters.shot);
			}
			if (shareType === SHARE_TYPE.SLIDE_NOTE) {
				const teleprompter = teleprompters.find((tp) => tp.id === selectedTeleprompters.media);
				return media.find((m) => m.id === teleprompter?.media_id);
			}
		}

		if (forGlobalNote) {
			return workingAllNote;
		}

		return selectedMedia;
	}, [
		forNote,
		forGlobalNote,
		selectedMedia,
		workingAllNote,
		workingNote,
		teleprompters,
		selectedTeleprompters,
		media,
	]);

	function isShotNote(object: any): object is TeleprompterInterface {
		return shareType === SHARE_TYPE.SHOT_NOTE;
	}

	function isMedia(object: any): object is MediaItem {
		return shareType === SHARE_TYPE.MEDIA || shareType === SHARE_TYPE.SLIDE_NOTE;
	}

	function isCreator(userToCheck?: number): Boolean {
		if (isShotNote(selectedItem)) {
			if (userToCheck) {
				return selectedItem?.gr_user === userToCheck;
			}
			return selectedItem?.gr_user === user?.gr_user;
		}
		if (userToCheck) {
			return selectedItem?.created_by === userToCheck;
		}
		// Decoupling migration strategy: include "brandlive_profile" here
		// because pre-decoupled "shared with" data could contain
		// "brandlive_profile" IDs before "gr_user" IDs existed.
		return selectedItem?.created_by === user!.gr_user
		|| selectedItem?.created_by === user!.brandlive_profile;
	}

	// in addition to creators, producers can share media items
	const isSharingMedia = useMemo(
		() => [SHARE_TYPE.MEDIA, SHARE_TYPE.SLIDE_NOTE].includes(shareType) && (isCreator() || host),
		[shareType, host],
	);

	function hide() {
		setHideShowAll(true);
		setTimeout(() => {
			setShowAll(false);
			setHideShowAll(false);
		}, 300);
	}

	function show() {
		setShowAll(true);
	}

	// shared with for notes is returned as a string intead of array of strings
	// probably should be refactored later
	const getSelectedItemSharedWith = (): number[] => {
		if (!selectedItem || !selectedItem.shared_with) {
			return [];
		}
		if (typeof selectedItem.shared_with === 'string') {
			return listToArray(selectedItem.shared_with);
		}
		return selectedItem.shared_with;
	};

	const possibleUsers = useMemo(() => uniqueArray('gr_user', [
		...invitations.map((invitation: Invitation) => invitation.profile),
		...connectedUsers,
	]), [connectedUsers, invitations]);

	const selectedShare = useMemo(
		() => getSelectedItemSharedWith().filter((num: number) => !!num) || [],
		[selectedItem],
	);

	const sharedUserMap = useMemo(() => {
		const obj: UserMap = {};
		const len = selectedShare.length;

		for (let i = 0; i < len; i += 1) {
			// Decoupling migration strategy: pre-decoupling shared with users
			// used "brandlive_profile" before "gr_user" existed
			const sharedUser = possibleUsers.find(
				(_user: ConnectedUser) => (_user.gr_user === selectedShare[i]
					|| _user.brandlive_profile === selectedShare[i]
				),
			);
			if (sharedUser) {
				obj[String(selectedShare[i])] = sharedUser;
			}
		}

		return obj;
	}, [possibleUsers, selectedShare]);

	function toggleSharePopover() {
		// Only the user who created the item can share it with others, no re-sharing.
		// Decoupling migration strategy: pre-decoupling shared with users
		// used "brandlive_profile" before "gr_user" existed
		if (isCreator() || isSharingMedia) {
			if (sharePopoverOpen) {
				setSharePopoverOpen(false);
				popoverToggled(false);
				setTimeout(() => {
					setSharePopoverHidden(true);
				}, 150);
			} else {
				setSharePopoverHidden(false);

				setTimeout(() => {
					setSharePopoverOpen(true);
					popoverToggled(true);
				});
			}
		}
	}

	// Used for comparing prevProps to current props
	const usePrevious = <T extends {}>(value: T): T | undefined => {
		const ref = useRef<T>();
		useEffect(() => {
			ref.current = value;
		});
		return ref.current;
	};
	const prevSelectedItem = usePrevious({ selectedItem });

	useEffect(() => {
		if (
			prevSelectedItem?.selectedItem
			&& prevSelectedItem?.selectedItem?.shared_with !== selectedItem?.shared_with
		) {
			// Detected user updated shared presentation with users. Save and send socket message.

			if (!token || !selectedMedia || !user) return;

			if (!forNote && !forGlobalNote) {
				dispatch(MediaActions.saveMediaItem(
					token,
					{
						...selectedMedia,
						edited_by: user.gr_user,
					},
					undefined,
				));
				socket!.mediaItemUpdate({ ...selectedMedia, edited_by: user.gr_user });
			} else if (forNote && workingNote) {
				dispatch(NotesActions.saveNote(token, workingNote));
			} else if (forGlobalNote && workingAllNote) {
				dispatch(NotesActions.saveNote(token, workingAllNote));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedItem, selectedMedia, forNote, forGlobalNote, workingNote, workingAllNote]);

	function getSelectedItemCreator(): ConnectedUser | Profile | null {
		if (isShotNote(selectedItem)) {
			return getUserProfileWithId(selectedItem.gr_user, connectedUsers, invitations);
		}
		if (selectedItem?.created_by) {
			return getUserProfileWithId(selectedItem.created_by, connectedUsers, invitations);
		}
		return null;
	}

	const sharedWithArray = useMemo(() => {
		if (selectedItem && selectedItem.shared_with) {
			const arr = getSelectedItemSharedWith();

			// Decoupling migration strategy: include "brandlive_profile" here
			// because pre-decoupled "shared with" data could contain
			// "brandlive_profile" IDs before "gr_user" IDs existed.
			const possibleUserIds = [
				...possibleUsers.map((pUser: any) => pUser.gr_user),
				...possibleUsers.map((pUser: any) => pUser.brandlive_profile),
			];
			return arr.filter((profile: number) => possibleUserIds.includes(profile));
		}
		return [];
	}, [selectedItem, possibleUsers]);

	function displayPlusAvatar() {
		if (isCreator() && sharedWithArray.length > 3) {
			return true;
		}
		if (sharedWithArray.length > 3) {
			return true;
		}
		return false;
	}

	const closeSharePopover = (currentlySelected?: number[]) => {
		if (currentlySelected) {
			// sharing teleprompter action is wrapped in a hook
			if (shareType === SHARE_TYPE.SHOT_NOTE && selectedTeleprompters.shot) {
				shareTeleprompter(selectedTeleprompters.shot, currentlySelected);
			} else if (token && shareType === SHARE_TYPE.SLIDE_NOTE && isMedia(selectedItem)) {
				dispatch(MediaActions.saveMediaItem(
					token,
					{
						...selectedItem,
						edited_by: user?.gr_user,
						shared_with: currentlySelected.join(','),
					},
					undefined,
				));
			} else if (!forNote && !forGlobalNote) {
				dispatch(MediaActions.setSharedWith(currentlySelected.join(',')));
			} else if (forNote) {
				dispatch(NotesActions.setSharedWith(currentlySelected.join(',')));
			} else if (forGlobalNote) {
				dispatch(NotesActions.setSharedWithGlobal(currentlySelected.join(',')));
			}
		}
		setSharePopoverOpen(false);
		popoverToggled(false);
		setTimeout(() => {
			setSharePopoverHidden(true);
		}, 150);
	};

	return (
		<div className={classNames('share-sharebar', { notes: forNote || forGlobalNote })}>
			<>
				{meeting ? (
					<>
						{(selectedShare.length === 0 && (isCreator() || isSharingMedia)) ? (
							<>
								{isSharingMedia && (
									<div className={classNames('presenters', isCreator() && 'creator', { 'show-all': showAll })} onMouseLeave={hide}>
										{<div className={classNames('avatar-container', sharePopoverOpen && 'dropdown-open')}>
											{selectedItem && (
												<Avatar
													user={getSelectedItemCreator() as AvatarUser}
													noBorder={true}
													size={'thirtytwo'}
												/>
											)}
										</div>}
									</div>
								)}
								<button onClick={toggleSharePopover} className={classNames('open-share-popover', sharePopoverOpen && 'open')}>
									<Icon name={ICONS.SHARE} color={COLORS.GRAY} size={16}></Icon>
								</button>
							</>
						) : (
							<>
								<div className={classNames('presenters', isCreator() && 'creator', { 'show-all': showAll })} onMouseLeave={hide}>
									{<div className={classNames('avatar-container', sharePopoverOpen && 'dropdown-open')}>
										{selectedItem && <Avatar
											// Decoupling migration strategy: include "brandlive_profile" here
											// because pre-decoupled "shared with" data could contain
											// "brandlive_profile" IDs before "gr_user" IDs existed.
											user={getSelectedItemCreator() as AvatarUser}
											noBorder={true}
											size={'thirtytwo'}
										/>}
									</div>}
									{sharedWithArray.map((id: number, index: number) => {
										if ((index < 2 || sharedWithArray.length === 3) && !!sharedUserMap[id]) {
											return (
												<div key={index} className="avatar-container">
													<Avatar
														user={sharedUserMap[id]}
														noBorder={true}
														size={'thirtytwo'}
													/>
												</div>
											);
										}
										return undefined;
									})}
									{selectedItem?.shared_with && displayPlusAvatar() && (
										<div className="avatar-container" onClick={show}>
											<div className="avatar-placeholder">
												+{sharedWithArray.length > 2 && sharedWithArray.length - 2}
											</div>
										</div>
									)}
									{showAll && (
										<div className={classNames('show-all-container', { hiding: hideShowAll })}>
											<div className={classNames('avatar-container', sharePopoverOpen && 'dropdown-open')}>
												{selectedItem && <Avatar
													user={getSelectedItemCreator() as AvatarUser}
													noBorder={true}
													size={'thirtytwo'}
												/>}
											</div>
											{selectedItem?.shared_with && (
												sharedWithArray.map((id: number, index: number) => {
													if (sharedUserMap[id]) {
														return (
															<div key={index} className="avatar-container">
																<Avatar
																	user={sharedUserMap[id]}
																	noBorder={true}
																	size={'thirtytwo'}
																/>
															</div>
														);
													}
													return undefined;
												})
											)}
										</div>
									)}
								</div>
								{(isCreator() || isSharingMedia) && (
									<button onClick={toggleSharePopover} className={classNames('open-share-popover', sharePopoverOpen && 'open')}>
										<Icon name={ICONS.SHARE} color={COLORS.GRAY} size={16}></Icon>
									</button>
								)}
							</>
						)}
					</>
				) : (
					<>
						<span>Enter a meeting to share notes</span>
					</>
				)}
			</>
			<ShareList
				shareListOpen={sharePopoverOpen}
				shareListHidden={sharePopoverHidden}
				closeShareList={closeSharePopover}
				selectedItem={selectedItem}
				creatorId={isShotNote(selectedItem) ? selectedItem?.gr_user : selectedItem?.created_by}
				shareType={shareType}
				constrainToParent={constrainToParent}
			/>
		</div>
	);
}
